import { useState } from "react";
import { C, Card, CardHead, SectionTitle, Btn, Badge, Modal, Field, statusBadge, SshCopyBlock } from "./theme";
import { POST, PATCH, DELETE } from "./api";
import { useLang } from "./i18n";

export default function UserServices({ me, vms, services, getServerIp, fetchAll }) {
  const { t } = useLang();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ vm_id: "", name: "", description: "", vm_port: "", host_port: "", access: "tunnel" });
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState({});
  const [deleting, setDeleting] = useState({});

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const vmMap = {};
  (vms || []).forEach(vm => { vmMap[vm.id] = vm; });

  const grouped = {};
  (vms || []).forEach(vm => { grouped[vm.id] = []; });
  (services || []).forEach(s => {
    if (grouped[s.vm_id]) grouped[s.vm_id].push(s);
  });

  const openAdd = () => {
    setForm({ vm_id: vms && vms.length > 0 ? vms[0].id : "", name: "", description: "", vm_port: "", host_port: "", access: "tunnel" });
    setShowAdd(true);
  };

  const addService = async () => {
    if (!form.vm_id || !form.name.trim() || !form.vm_port) return alert("필수 항목을 입력하세요");
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      vm_port: parseInt(form.vm_port, 10),
      access: form.access,
    };
    if (form.access === "external") {
      if (!form.host_port) return alert("외부 접근용 호스트 포트를 입력하세요");
      payload.host_port = parseInt(form.host_port, 10);
      const vm = vmMap[form.vm_id];
      if (vm && (payload.host_port < vm.port_range_start || payload.host_port > vm.port_range_end)) {
        return alert(`호스트 포트는 ${vm.port_range_start}~${vm.port_range_end} 범위여야 합니다`);
      }
    }
    setSubmitting(true);
    try {
      await POST(`/api/vms/${form.vm_id}/services`, payload);
      await fetchAll();
      setShowAdd(false);
    } catch (e) { alert(e.message); }
    setSubmitting(false);
  };

  const toggleService = async (svcId, currentStatus) => {
    const newStatus = currentStatus === "running" ? "stopped" : "running";
    setToggling(p => ({ ...p, [svcId]: true }));
    try {
      await PATCH(`/api/services/${svcId}`, { status: newStatus });
      await fetchAll();
    } catch (e) { alert(e.message); }
    setToggling(p => ({ ...p, [svcId]: false }));
  };

  const removeService = async (svcId) => {
    if (!window.confirm("이 서비스를 삭제하시겠습니까?")) return;
    setDeleting(p => ({ ...p, [svcId]: true }));
    try {
      await DELETE(`/api/services/${svcId}`);
      await fetchAll();
    } catch (e) { alert(e.message); }
    setDeleting(p => ({ ...p, [svcId]: false }));
  };

  const tunnelCmd = (svc, vm) => {
    const ip = getServerIp(vm.server);
    return `ssh -L ${svc.vm_port}:localhost:${svc.vm_port} -p ${vm.ssh_port} ${me.id}@${ip} -N`;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <SectionTitle
        title={t("service_ports_tab")}
        action={<Btn small variant="cyan" onClick={openAdd} disabled={!vms || vms.length === 0}>{t("add")}</Btn>}
      />

      {(!vms || vms.length === 0) ? (
        <div style={{ fontSize: 12, color: C.dim2, fontFamily: "monospace", textAlign: "center", padding: 20 }}>
          VM이 없습니다
        </div>
      ) : (
        Object.entries(grouped).map(([vmId, svcs]) => {
          const vm = vmMap[vmId];
          if (!vm) return null;
          return (
            <Card key={vmId}>
              <CardHead>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 12, fontFamily: "monospace", color: C.text, fontWeight: 700 }}>{vmId}</span>
                  <Badge label={`포트 ${vm.port_range_start}~${vm.port_range_end}`} color={C.blue} />
                </div>
              </CardHead>
              <div style={{ padding: "12px 16px" }}>
                {svcs.length === 0 ? (
                  <div style={{ fontSize: 11, color: C.dim2, fontFamily: "monospace", textAlign: "center", padding: 10 }}>
                    {t("no_services")}
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {svcs.map(svc => (
                      <div key={svc.id} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "10px 12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 12, color: C.text, fontFamily: "monospace", fontWeight: 700 }}>{svc.name}</span>
                            {statusBadge(svc.status)}
                            <Badge label={svc.access} color={svc.access === "external" ? C.amber : C.cyan} />
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <Btn small variant={svc.status === "running" ? "amber" : "green"}
                              disabled={toggling[svc.id]}
                              onClick={() => toggleService(svc.id, svc.status)}>
                              {toggling[svc.id] ? "…" : svc.status === "running" ? t("stop") : t("start")}
                            </Btn>
                            <Btn small variant="red" disabled={deleting[svc.id]} onClick={() => removeService(svc.id)}>
                              {deleting[svc.id] ? "…" : t("delete")}
                            </Btn>
                          </div>
                        </div>
                        {svc.description && (
                          <div style={{ fontSize: 10, color: C.dim2, fontFamily: "monospace", marginBottom: 6 }}>{svc.description}</div>
                        )}
                        <div style={{ display: "flex", gap: 16, fontSize: 10, color: C.text, fontFamily: "monospace", marginBottom: 6 }}>
                          <span>{t("vm_port")}: <span style={{ color: C.cyan }}>{svc.vm_port}</span></span>
                          {svc.host_port && <span>{t("host_port")}: <span style={{ color: C.amber }}>{svc.host_port}</span></span>}
                        </div>
                        {svc.access === "tunnel" && (
                          <SshCopyBlock label="SSH 터널 명령어" text={tunnelCmd(svc, vm)} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          );
        })
      )}

      {/* Add Service Modal */}
      {showAdd && (
        <Modal title={t("add")} onClose={() => setShowAdd(false)}>
          <Field label={t("vm")} value={form.vm_id} onChange={v => set("vm_id", v)}
            options={(vms || []).map(vm => ({ v: vm.id, l: `${vm.id} (${vm.server})` }))} />
          <Field label={t("service_name")} value={form.name} onChange={v => set("name", v)} placeholder="jupyter" />
          <Field label={t("service_name")} value={form.description} onChange={v => set("description", v)} placeholder="" />
          <Field label={t("vm_port")} value={form.vm_port} onChange={v => set("vm_port", v)} type="number" placeholder="8888" />
          <Field label={t("access")} value={form.access} onChange={v => set("access", v)}
            options={[{ v: "tunnel", l: t("tunnel") }, { v: "external", l: t("external") }]} />
          {form.access === "external" && (
            <>
              <Field label={t("host_port")} value={form.host_port} onChange={v => set("host_port", v)} type="number"
                placeholder={form.vm_id && vmMap[form.vm_id]
                  ? `${vmMap[form.vm_id].port_range_start}~${vmMap[form.vm_id].port_range_end}`
                  : "호스트 포트"} />
              {form.vm_id && vmMap[form.vm_id] && (
                <div style={{ fontSize: 9, color: C.amber, fontFamily: "monospace", marginTop: -8, marginBottom: 10 }}>
                  허용 범위: {vmMap[form.vm_id].port_range_start} ~ {vmMap[form.vm_id].port_range_end}
                </div>
              )}
            </>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>{t("cancel")}</Btn>
            <Btn variant="cyan" disabled={submitting} onClick={addService}>
              {submitting ? "..." : t("add")}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
